import { App } from 'antd';
import type {
  NotificationInstance,
  ArgsProps,
} from 'antd/es/notification/interface';
import { logger } from './logger';

/**
 * Toast notification types
 */
export type ToastType = 'success' | 'error' | 'info' | 'warning';

/**
 * Toast configuration options
 */
export interface ToastConfig {
  /** Type of notification */
  type: ToastType;
  /** Title of the notification */
  title: string;
  /** Description/message content */
  message: string;
  /** Custom icon (optional) */
  icon?: React.ReactNode;
  /** Duration in seconds (default: 3) */
  duration?: number;
  /** Placement (default: 'topRight') */
  placement?: ArgsProps['placement'];
  /** Show close button (default: true) */
  closable?: boolean;
}

/**
 * Default toast configuration
 */
const DEFAULT_CONFIG: Partial<ToastConfig> = {
  duration: 3,
  placement: 'topRight',
  closable: true,
};

/**
 * Notification service singleton
 * Stores the notification instance from App component
 */
class NotificationService {
  private static instance: NotificationInstance | null = null;

  static setInstance(notificationInstance: NotificationInstance) {
    this.instance = notificationInstance;
  }

  static showToast(config: ToastConfig) {
    if (!this.instance) {
      logger.warn(
        'Notification instance not initialized. Make sure App component is wrapped with AntApp.',
      );
      return;
    }

    const notificationConfig = {
      title: config.title,
      description: config.message,
      icon: config.icon,
      duration: config.duration ?? DEFAULT_CONFIG.duration,
      placement: config.placement ?? DEFAULT_CONFIG.placement,
      closable: config.closable ?? DEFAULT_CONFIG.closable,
    };

    switch (config.type) {
      case 'success':
        this.instance.success(notificationConfig);
        break;
      case 'error':
        this.instance.error(notificationConfig);
        break;
      case 'info':
        this.instance.info(notificationConfig);
        break;
      case 'warning':
        this.instance.warning(notificationConfig);
        break;
      default:
        this.instance.info(notificationConfig);
    }
  }

  static closeAll() {
    if (this.instance) {
      this.instance.destroy();
    }
  }
}

/**
 * Custom hook for showing notifications using Ant Design App component
 *
 * IMPORTANT: Must be used within App component context
 *
 * @example
 * const { showToast } = useNotification();
 * showToast({ type: 'success', title: 'Success', message: 'Operation completed' });
 * showToast({ type: 'error', title: 'Error', message: 'Something went wrong' });
 * showToast({ type: 'info', title: 'Info', message: 'This is information' });
 * showToast({ type: 'warning', title: 'Warning', message: 'Please be careful' });
 */
export const useNotification = () => {
  const { notification } = App.useApp();

  // Initialize the notification service instance
  NotificationService.setInstance(notification);

  const showToast = (config: ToastConfig) => {
    const notificationConfig = {
      title: config.title,
      description: config.message,
      icon: config.icon,
      duration: config.duration ?? DEFAULT_CONFIG.duration,
      placement: config.placement ?? DEFAULT_CONFIG.placement,
      closable: config.closable ?? DEFAULT_CONFIG.closable,
    };

    switch (config.type) {
      case 'success':
        notification.success(notificationConfig);
        break;
      case 'error':
        notification.error(notificationConfig);
        break;
      case 'info':
        notification.info(notificationConfig);
        break;
      case 'warning':
        notification.warning(notificationConfig);
        break;
      default:
        notification.info(notificationConfig);
    }
  };

  const closeAllNotifications = () => {
    notification.destroy();
  };

  return { showToast, closeAllNotifications };
};

/**
 * Show toast notification (can be used anywhere)
 *
 * @remarks
 * - In React components: Prefer using `useNotification()` hook for better context integration
 * - In services/stores/utils: Use this function directly
 * - Automatically initializes notification instance from App component context
 *
 * @example
 * ```tsx
 * // In components (preferred)
 * const { showToast } = useNotification();
 * showToast({ type: 'success', title: 'Success', message: 'Done!' });
 *
 * // In services/stores (use direct import)
 * import { showToast } from '@/shared/utils';
 * showToast({ type: 'error', title: 'Error', message: 'Failed!' });
 * ```
 */
export const showToast = (config: ToastConfig) => {
  NotificationService.showToast(config);
};

/**
 * Close all active notifications
 *
 * @remarks
 * Can be used in components or services to clear all notifications
 *
 * @example
 * ```tsx
 * import { closeAllNotifications } from '@/shared/utils';
 * closeAllNotifications();
 * ```
 */
export const closeAllNotifications = () => {
  NotificationService.closeAll();
};
